import type { GetStaticPaths, NextPage } from 'next'
import dynamic from 'next/dynamic'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Waypoint } from 'react-waypoint'
import styled from 'styled-components'

import { ApiHandler } from '@hozana/api/ApiHandler'
import { removeEntity } from '@hozana/api/actions'
import { fetchApi } from '@hozana/api/fetchApi'
import { modifyQueryEntities } from '@hozana/api/functions'
import { useApiData } from '@hozana/api/hooks/useApiData'
import { useUpdateRequest } from '@hozana/api/hooks/useUpdateRequest'
import { createGetServerSideData, createGetServerSideProps } from '@hozana/api/server/serverSide'
import { createGetStaticData, createGetStaticProps } from '@hozana/api/server/static'
import { useAction } from '@hozana/hooks/useAction'
import { useDispatch, useSelector } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { getFirstQueryValue, getFullUrl, getQueryValues, translateUrl } from '@hozana/router/functions'
import { useScreen } from '@hozana/screen/useScreen'
import { IMAGE_SIZES, getImageUrl } from '@hozana/styling/image'
import { QA } from '@hozana/tests/constants'
import { EVENT_ORIGIN, TRACKING_SOURCE, UTM } from '@hozana/tracking/constants'

import { Container } from 'elements/layout/Container'
import { Div } from 'elements/layout/Div'
import { Space } from 'elements/layout/Space'

import { openPopin } from 'general/actions'
import { QUERY_ACTION } from 'general/constants'
import { POPINS } from 'general/managers/popins/constants'
import { Head } from 'general/structure/Head'
import { Page } from 'general/structure/Page'
import { TabsBar } from 'general/structure/Tabs/TabsBar'
import { useTabs } from 'general/structure/Tabs/useTabs'
import { NAMESPACE } from 'i18n/constants'
import { PAGE } from 'routes/constants'

import { PreviewingCommunityBanner } from 'modules/community/components/PreviewingCommunityBanner'
import { COMMUNITY_PAGE_TABS } from 'modules/community/constants'
import {
  communityNameTitleGetter,
  getUserRole,
  isCommunityPublished,
  redirectToCommunitySubscription,
} from 'modules/community/functions'
import { useCommunitiesPropositions } from 'modules/community/hooks/useCommunitiesPropositions'
import { CommunityPageTopSection } from 'modules/community/pages/CommunityPage/CommunityPageTopSection'
import { GoogleMapSection } from 'modules/community/pages/CommunityPage/GoogleMapSection'
import { getCommunityQuery, incrementCommunityViewsCountMutation } from 'modules/community/queries'
import { leaveCommunity, showCommunityNotifications } from 'modules/community/routines'
import { selectCommunity } from 'modules/community/selectors'
import type { TCommunity } from 'modules/community/types'
import { PUBLICATION_FILTERS } from 'modules/publication/constants'
import { SHARE_OBJECTS } from 'modules/share/constants'
import { getTagsQuery } from 'modules/tag/queries'
import { selectAllTags } from 'modules/tag/selectors'
import { useUser } from 'modules/user/hooks/useUser'

import { CommunityDescriptionTab } from './CommunityDescriptionTab'
import { CommunityPageDesktopContent } from './CommunityPageDesktopContent'
import { CommunityPageMobileContent } from './CommunityPageMobileContent'
import { PreloadCommunityPage } from './PreloadCommunityPage'

const PublicationsListTab = dynamic(() => import('./PublicationsListTab').then((c) => c.PublicationsListTab))

type TStyledDivProps = { hide?: boolean }

const StyledDiv = styled(Div)<TStyledDivProps>`
  ${(props) => props.hide && 'display: none;'};
`

const publicationMaxWidth = '712px'
const communityImageSize = IMAGE_SIZES.community.big

const getTabsBarMaxWidth = (nbTabs: number) => ({
  xs: 'initial',
  sm: nbTabs === 3 ? publicationMaxWidth : '400px',
  lg: publicationMaxWidth,
})

/** Get canonical link for the head html tag */
const getHeadLinks = (community: TCommunity) => ({
  canonical: getFullUrl(
    {
      pathname: PAGE.COMMUNITY_VIEW,
      query: {
        communityId: community.id,
        communitySlug: community.slug,
      },
    },
    community.lang,
  ),
})

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' })

const getCommunityPageApiQueries = (communityId: number) => ({
  community: {
    query: modifyQueryEntities(getCommunityQuery({ communityId }), {
      community: (community) =>
        community.id === communityId ? community : { ...community, description: undefined, prayerContent: undefined },
    }),
    displayErrors: true,
  },
  // Tags must be included in html: tags links are SEO links
  tags: {
    query: getTagsQuery(),
    noDataToReturn: true,
  },
})

export const getStaticProps = createGetStaticProps(
  PAGE.COMMUNITY_VIEW,
  communityNameTitleGetter,
  async (ctx) => {
    const communityId = Number(ctx.params.communityId)

    const data = await createGetStaticData(() => getCommunityPageApiQueries(communityId))(ctx)

    // Redirect to the right language if current locale is not community.lang
    if ('props' in data) {
      const community = selectCommunity(ctx.store.getState(), communityId)
      if (community && community.lang !== ctx.locale) {
        //return getRedirect(community)
      }

      // Remove useless tags
      const tags = selectAllTags(ctx.store.getState())
      tags.forEach(({ id }) => {
        if (!community.tags?.includes(id)) {
          ctx.store.dispatch(removeEntity('tag', id))
        }
      })
    }
    return data
  },
  { namespaces: [NAMESPACE.COMMUNITY, NAMESPACE.PUBLICATION, NAMESPACE.PRAY] },
)

// For admin
export const getServerSideProps = createGetServerSideProps(
  PAGE.COMMUNITY_VIEW_UNPUBLISHED,
  communityNameTitleGetter,
  async (ctx) => {
    const communityId = Number(ctx.params.communityId)

    const data = await createGetServerSideData(() => getCommunityPageApiQueries(communityId))(ctx)

    // Redirect to the published url
    if ('props' in data) {
      const community = selectCommunity(ctx.store.getState(), communityId)
      if (community && isCommunityPublished(community)) {
        //return getRedirect(community)
      }
    }
    return data
  },
  { namespaces: [NAMESPACE.COMMUNITY, NAMESPACE.PUBLICATION, NAMESPACE.PRAY] },
)

/**
 * ### CommunityPage ###
 */
export const CommunityPage: NextPage = () => {
  const {
    i18n: { language },
    t,
  } = useTranslation()
  const screen = useScreen()
  const { pathname, query, replace } = useRouter() // #router.replace
  const dispatch = useDispatch()
  const me = useUser()
  const { communityId: stringCommunityId, communitySlug, ...params } = getFirstQueryValue(query)
  const communityId = parseInt(stringCommunityId, 10)

  // Increment community views count
  useEffect(() => {
    if (communityId) {
      dispatch(fetchApi(incrementCommunityViewsCountMutation({ communityId })))
    }
  }, [communityId, dispatch])

  const [, communityQuery] = useApiData(
    () => ({
      query: getCommunityQuery({ communityId }),
      disabled: !communityId || !me.isLogged,
    }),
    [communityId, me.isLogged],
  )
  const community = useSelector((state) => selectCommunity(state, communityId))

  /** The community are not up to date for logged in users as long as we don't have the refreshed data */
  const communityUpToDate = useUpdateRequest(communityQuery)

  const [isMapDisplayed, setIsMapDisplayed] = useState(false)

  const communityImage = community && getImageUrl(community.picture, communityImageSize)
  const communityImageWidth = community && communityImageSize.split('x')[0]
  const communityImageHeight = community && communityImageSize.split('x')[1]
  const roles = getUserRole(community)

  const tabList = {
    [COMMUNITY_PAGE_TABS.DESCRIPTION]: 'trans:community:community.tab.description',
    [COMMUNITY_PAGE_TABS.PUBLICATIONS]: t('community:community.tab.publications', {
      nbPublications: community?.nbPublications || '0',
    }),
    ...((roles?.hasRole || me.admin) && {
      [COMMUNITY_PAGE_TABS.SCHEDULED_AND_DRAFT]: 'trans:community:community.tab.scheduled-and-drafts',
    }),
  }

  const { currentTab, changeTab, getTabUrl } = useTabs({ data: tabList })

  useAction(
    () =>
      dispatch(
        openPopin(POPINS.InvitePopin, {
          objectToShare: SHARE_OBJECTS.COMMUNITY,
          community,
          utmContent: UTM.CONTENT.COMMUNITY_PAGE_URL_INVITE_POPIN,
          trackingSource: TRACKING_SOURCE.EMAIL_LINK,
        }),
      ),
    [community, dispatch],
    QUERY_ACTION.INVITE,
    { disabled: !community },
  )

  useAction(
    () => {
      if (!getUserRole(community).isMember) {
        redirectToCommunitySubscription(community, me.isLogged, {
          course: 'page',
          origin: EVENT_ORIGIN.AUTOMATIC,
          source: params.source ?? TRACKING_SOURCE.EMAIL_LINK,
        })
      }
    },
    [community, me.isLogged, params],
    QUERY_ACTION.JOIN,
    { disabled: !community },
  )

  useAction(
    () => dispatch(leaveCommunity({ community, trackingSource: TRACKING_SOURCE.EMAIL })),
    [community, dispatch],
    QUERY_ACTION.LEAVE,
    { disabled: !community },
  )

  useAction(
    () => {
      if (roles.isMember) {
        dispatch(showCommunityNotifications({ community }))
      }
    },
    [community, dispatch, roles.isMember],
    QUERY_ACTION.SHOW_NOTIFICATIONS,
    { disabled: !community || !communityUpToDate },
  )

  useAction(
    () => {
      window.location.href = translateUrl(
        {
          pathname: PAGE.COMMUNITY_VIEW,
          query: {
            communityId: community?.id,
            communitySlug: community?.slug,
            utm_source: UTM.SOURCE.SHARE,
            utm_medium: UTM.MEDIUM.MAILTO,
            utm_campaign: UTM.CAMPAIGN.COMMUNITY(community?.id, community?.slug),
            utm_content: UTM.CONTENT.PUBLICATION_MAIL_MAILTO_SHARE_BUTTON,
          },
        },
        community?.lang,
        { format: 'string' },
      )
    },
    [community?.id, community?.lang, community?.slug],
    QUERY_ACTION.SHARE_MAIL_PUBLI,
    { disabled: !community },
  )

  // Ensure correct url
  useEffect(() => {
    if (community) {
      if (communitySlug !== community.slug) {
        replace({
          pathname,
          query: {
            communityId: community.id,
            communitySlug: community.slug,
            ...getQueryValues(window.location),
          },
        })
      }
    }
  }, [community, communitySlug, pathname, replace])

  useCommunitiesPropositions({ community })

  // If no community slug is defined, wait for page reload before rendering
  if (!communitySlug) return null

  const tabContent =
    currentTab === COMMUNITY_PAGE_TABS.DESCRIPTION ? (
      <CommunityDescriptionTab community={community} />
    ) : (
      <Div data-testid={QA.COMMUNITY_PAGE.PUBLICATIONS.WRAPPER} maxW="712px" m="0 auto 90px">
        <PublicationsListTab
          community={community}
          filter={
            currentTab === COMMUNITY_PAGE_TABS.PUBLICATIONS
              ? PUBLICATION_FILTERS.PUBLISHED
              : PUBLICATION_FILTERS.SCHEDULED_AND_DRAFTS
          }
        />
      </Div>
    )

  return (
    <Page>
      <ApiHandler
        queries={[communityQuery]}
        display="fullPage"
        onRetry={() => {
          communityQuery.refetch()
        }}
        noLoadingDisplay
      />
      {!community && communityQuery.loading && <PreloadCommunityPage tabList={tabList} />}
      {community && (
        <main>
          <PreviewingCommunityBanner community={community} />
          <article>
            <Head
              description={community.introduction}
              isIndexed={
                community.isSeoIndexed !== false &&
                community.lang === language &&
                currentTab === COMMUNITY_PAGE_TABS.DESCRIPTION
              }
              title={community.name}
              image={community.picture && communityImage}
              imageWidth={community.picture && communityImageWidth}
              imageHeight={community.picture && communityImageHeight}
              links={community ? getHeadLinks(community) : undefined}
              rss={{
                title: t('community:publication.publications.into.community', { community: community.name }),
                href: `${CONF_KEYS.API_URL}/${language}/rss/community/${community.id}/publications`,
              }}
            />

            {/* CONTENT */}
            <Container p={{ xs: 0, lg: '20px 10px 0' }} maxW={{ xs: 1, lg: '1010px' }}>
              <CommunityPageTopSection
                community={community}
                communityUpToDate={communityUpToDate}
                tabsBar={
                  <TabsBar
                    changeTab={changeTab}
                    currentTab={currentTab}
                    data={tabList}
                    getTabUrl={getTabUrl}
                    maxW={tabList && getTabsBarMaxWidth(Object.keys(tabList).length)}
                    dataQaIds={QA.COMMUNITY_PAGE.TABS(tabList)}
                    asButton
                  />
                }
              />
              {screen.xs || screen.sm || screen.md ? (
                <CommunityPageMobileContent community={community}>
                  {currentTab === COMMUNITY_PAGE_TABS.PUBLICATIONS && (screen.sm || screen.md) && <Space />}
                  {tabContent}
                </CommunityPageMobileContent>
              ) : (
                <CommunityPageDesktopContent community={community}>{tabContent}</CommunityPageDesktopContent>
              )}
            </Container>

            {/* GOOGLE MAP (only visible if the current tab is description, to allow infinite scroll) */}
            <StyledDiv as="aside" w="100%" hide={currentTab !== COMMUNITY_PAGE_TABS.DESCRIPTION} h="50vh" maxH="450px">
              <Waypoint scrollableAncestor="window" bottomOffset="-50px" onEnter={() => setIsMapDisplayed(true)}>
                {isMapDisplayed ? <GoogleMapSection community={community} /> : <div />}
              </Waypoint>
            </StyledDiv>
          </article>
        </main>
      )}
    </Page>
  )
}
